import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams, useHistory } from "react-router-dom";
import { getImportStockUpdateById } from "../../../services/Import";
import {
  Grid,
  Paper,
  Typography,
  Divider,
  Box,
  Tooltip,
  IconButton,
  Button,
  Card,
  CardContent,
  Chip,
  useTheme,
  alpha,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Icons
import InventoryIcon from "@mui/icons-material/Inventory";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CategoryIcon from "@mui/icons-material/Category";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import DescriptionIcon from "@mui/icons-material/Description";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";

function StockUpdateView() {
  const theme = useTheme();
  const history = useHistory();
  const { intakeId } = useParams();
  const [stockIntake, setStockIntake] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await getImportStockUpdateById(intakeId);
      if (result) {
        setStockIntake(result);
      } else {
        setError("Failed to retrieve stock update data");
      }
    } catch (error) {
      console.error("Error fetching stock update data:", error);
      setError("An error occurred while fetching data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [intakeId]);

  const formatCurrency = (value) => {
    return typeof value === "number"
      ? `Rs: ${value.toFixed(2)}`
      : `Rs: ${parseFloat(value || 0).toFixed(2)}`;
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">
          Loading stock update details...
        </Typography>
      </Box>
    );
  }

  if (error || !stockIntake || !stockIntake.intakeId) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          m: 3,
          textAlign: "center",
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.error.light, 0.1),
        }}
      >
        <Box sx={{ mb: 2 }}>
          <DescriptionIcon color="error" sx={{ fontSize: 48 }} />
        </Box>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Stock Update Not Found
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {error ||
            "The requested stock update data could not be retrieved or does not exist."}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
        >
          Go Back
        </Button>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, md: 2 },
        m: { xs: 1, md: 2 },
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <InventoryIcon fontSize="large" color="primary" />
          <Typography variant="h5" fontWeight="600" color="primary.main">
            Stock Update Details
          </Typography>
        </Box>
        <Tooltip title="Close">
          <IconButton
            onClick={() => history.goBack()}
            aria-label="close"
            sx={{
              bgcolor: alpha(theme.palette.grey[500], 0.1),
              "&:hover": {
                bgcolor: alpha(theme.palette.grey[500], 0.2),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Product Information */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <ShoppingCartIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Product Information
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Item Name
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {stockIntake.imports.itemName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Brand
                  </Typography>
                  <Typography variant="body1">
                    {stockIntake.imports.brand}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Product Code
                  </Typography>
                  <Typography variant="body1">
                    {stockIntake.imports.product_code}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <ReceiptIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Update Information
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Added By
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <PersonIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography variant="body1">
                      {stockIntake.addedBy}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Added Date
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CalendarTodayIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography variant="body1">
                      {stockIntake.addedDate}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Bill Number
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {stockIntake.billNo}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Note */}
      <Card elevation={1} sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <EditNoteOutlinedIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography
              variant="subtitle1"
              fontWeight="700"
              color="primary.main"
            >
              Note
            </Typography>
          </Box>
          <Box
            sx={{
              p: 2,
              bgcolor: alpha(theme.palette.background.default, 0.5),
              borderRadius: 1,
            }}
          >
            <Typography variant="body1" fontWeight="600">
              {stockIntake.note}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Categories */}
      <Card elevation={1} sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <CategoryIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography
              variant="subtitle1"
              fontWeight="700"
              color="primary.main"
            >
              Product Categories
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2,
              bgcolor: alpha(theme.palette.background.default, 0.5),
              borderRadius: 1,
            }}
          >
            <Grid container spacing={1}>
              {stockIntake.imports.im_categories &&
                stockIntake.imports.im_categories.map((category, index) => (
                  <Grid item key={index}>
                    <Chip
                      label={Object.entries(category)
                        .filter(([key]) => key !== "cat_id")
                        .map(([, value]) => value)
                        .filter(Boolean)
                        .join(" : ")}
                      variant="outlined"
                      sx={{
                        borderColor: theme.palette.primary.light,
                        bgcolor: alpha(theme.palette.primary.light, 0.1),
                        "& .MuiChip-label": {
                          px: 1,
                          fontSize: "0.85rem",
                          fontWeight: 500,
                        },
                      }}
                    />
                  </Grid>
                ))}
              {(!stockIntake.imports.im_categories ||
                stockIntake.imports.im_categories.length === 0) && (
                <Grid item xs={12}>
                  <Typography color="text.secondary" align="center">
                    No categories assigned
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Financial Information */}
      <Card elevation={1} sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <AttachMoneyIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography
              variant="subtitle1"
              fontWeight="700"
              color="primary.main"
            >
              Financial Details
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <ShoppingCartIcon sx={{ color: "info.main", fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary">
                    Quantity
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight="700" color="info.main">
                  {stockIntake.qty}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <LocalOfferIcon
                    sx={{ color: "primary.main", fontSize: 20 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Price Per Item
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight="700" color="primary.main">
                  {formatCurrency(stockIntake.pricePerItem)}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <AttachMoneyIcon
                    sx={{ color: "warning.main", fontSize: 20 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Discount (%)
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight="700" color="warning.main">
                  {stockIntake.discount || "0.00"}%
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <AttachMoneyIcon
                    sx={{ color: "success.main", fontSize: 20 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Total Price
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight="700" color="success.main">
                  {formatCurrency(stockIntake.totalPrice)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
        >
          Go Back
        </Button>
      </Box>
    </Paper>
  );
}

StockUpdateView.propTypes = {
  intakeId: PropTypes.any,
};

export default StockUpdateView;
